import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { overlay } from 'overlay-kit';
import '../constants/events.quiz-forced-school-grade';
import { trackEvent } from '@zep/logger/tracking';
import { getGetMyProfileQueryKey, useEditQuizUser, useGetMyProfile } from '@/api';
import { useUser } from '@/lib/auth';
import { ApiTokenStorage } from '@/module/auth';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { alertDialog } from '@/ui_v3';
import { type ForcedSchoolGradeFormValues, ForcedSchoolGradeModal } from '../components/ForcedSchoolGradeModal';

/**
 * 저장 실패 사유를 OTel-safe 문자열로 요약한다 (도메인 로컬 빌더,
 * docs/event-tracking/guide.md §2.3). PII 없이 http status 만 남긴다.
 */
const buildFailReason = (error: unknown): string => {
  if (isAxiosError(error)) {
    return typeof error.response?.status === 'number' ? `http_${error.response.status}` : 'network';
  }
  return 'unknown';
};

/**
 * 로그인 직후 닫기 불가능한 학교·학년 필수 입력 모달을 관리하는 훅.
 *
 * 대상: 교육 종사자(teacher) + 학교/학년 미입력 계정.
 *
 * ⚠️ 자격 판정 stub: "2026년 이전 가입" 조건은 현재 프로필 응답(MyProfileRes)에
 * 가입일(createdAt/registeredAt) 필드가 없어 프론트에서 도출할 수 없다.
 * 따라서 지금은 teacher + 학교/학년 미입력 조건만 게이트로 적용한다.
 * 가입일 + 교육종사자 여부 기반의 최종 게이트는 실제 백엔드 플래그로 확정해야 한다.
 */
export const useForcedSchoolGradeModal = () => {
  const {
    user,
    isAuthenticated,
    isInitialized,
    syncLatestUserInfo
  } = useUser();
  const isInExternalPlatform = useIsInExternalPlatform();
  const {
    data: userProfile,
    isLoading: isProfileLoading
  } = useGetMyProfile({
    query: {
      enabled: isAuthenticated
    }
  });
  const {
    mutateAsync: editQuizUser
  } = useEditQuizUser();
  const queryClient = useQueryClient();
  const hasOpenedRef = useRef(false);
  const overlayIdRef = useRef<string | null>(null);
  const handleSubmit = useCallback(async (values: ForcedSchoolGradeFormValues, close: () => void) => {
    trackEvent('quiz_forced_school_grade_modal_submit_click', {
      school_type_code: values.schoolTypeCode,
      school_grade_code: values.schoolGradeCode
    });
    try {
      await editQuizUser({
        data: {
          memberInfo: {
            teacherInfo: {
              institution: values.schoolName,
              schoolTypeCode: values.schoolTypeCode,
              schoolGradeCode: values.schoolGradeCode
            }
          }
        }
      });
      await queryClient.invalidateQueries({
        queryKey: getGetMyProfileQueryKey()
      });
      await syncLatestUserInfo(ApiTokenStorage.get());
      trackEvent('quiz_forced_school_grade_modal_save', {
        result: 'success',
        school_type_code: values.schoolTypeCode,
        school_grade_code: values.schoolGradeCode
      });
      close();
    } catch (error) {
      trackEvent('quiz_forced_school_grade_modal_save', {
        result: 'fail',
        reason: buildFailReason(error),
        school_type_code: values.schoolTypeCode,
        school_grade_code: values.schoolGradeCode
      });
      alertDialog.open({
        header: {
          title: '정보 저장에 실패했어요. 잠시 후 다시 시도해 주세요.'
        }
      });
      throw error;
    }
  }, [editQuizUser, queryClient, syncLatestUserInfo]);
  const openModal = useCallback(() => {
    const countryCode = userProfile?.data.countryCode;
    const teacherInfo = userProfile?.data.memberInfo?.teacherInfo;
    // 모달이 open 을 true 로 고정하므로 overlay 의 close()(=isOpen만 false)로는
    // 사라지지 않는다. 성공 시 즉시 언마운트되도록 unmount 를 넘긴다.
    // 반환된 id 는 로그아웃 시 강제 정리(teardown)에 쓴다.
    overlayIdRef.current = overlay.open(({
      unmount
    }) => <ForcedSchoolGradeModal onSubmit={values => handleSubmit(values, unmount)} countryCode={countryCode} />);
    trackEvent('quiz_forced_school_grade_modal_open', {
      country_code: countryCode,
      missing_school: !teacherInfo?.institution,
      missing_grade: !teacherInfo?.schoolGradeCode
    });
  }, [handleSubmit, userProfile]);
  const shouldShowModal = useCallback(() => {
    // LTI/Google Classroom 등 외부 플랫폼 임베드 컨텍스트에서는 노출하지 않는다.
    // (.claude/rules/external-platform-policy.md)
    if (isInExternalPlatform) return false;
    if (!isInitialized) return false;
    if (!isAuthenticated || !user) return false;
    // 회원가입/온보딩 미완료 유저는 제외한다. 가입 플로우(sign-up)에서 이미
    // 학교·학년을 입력받으므로, 그 위에 강제 모달을 겹쳐 띄우면 안 된다.
    if (!user.registrationStatus) return false;
    if (isProfileLoading) return false;
    // 한국 교육과정(학교급/학년) 기반 기능이라 KR 유저만 대상으로 한다. 비-KR 은 학교/학년
    // 데이터가 없어 닫을 수 없는 모달에서 갇힐 수 있다. 옵셔널 체이닝이라 프로필 미로드
    // (userProfile undefined) 시에도 !== 'KR' 로 걸러져, 로드 실패 시 오탐도 함께 막는다.
    if (userProfile?.data.countryCode !== 'KR') return false;
    if (user.type !== 'teacher') return false;
    const teacherInfo = userProfile?.data.memberInfo?.teacherInfo;
    const hasSchool = !!teacherInfo?.institution;
    const hasGrade = !!teacherInfo?.schoolGradeCode;
    if (hasSchool && hasGrade) return false;
    return true;
  }, [isInExternalPlatform, isInitialized, isAuthenticated, isProfileLoading, user, userProfile]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      openModal();
    }
  }, [shouldShowModal, openModal]);

  // 모달은 명령형으로 열리고 저장 성공 시에만 스스로 닫힌다. 그래서 열려 있는 동안
  // 세션 만료/중복 로그인/타 탭 로그아웃 등으로 로그아웃되면 모달이 로그아웃 화면 위에
  // 남는다. 인증이 풀리면 강제로 정리하고, 재로그인 시 다시 판정되도록 플래그를 되돌린다.
  useEffect(() => {
    if (isAuthenticated) return;
    if (overlayIdRef.current) {
      overlay.unmount(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    hasOpenedRef.current = false;
  }, [isAuthenticated]);
};